import React, {useEffect, useState, useMemo} from "react";
import {Form, Modal, Table, Input, Button, message, Pagination} from "antd";
import {useFormik} from "formik";
import * as yup from "yup";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../../hooks/useStore";
import {arrayAlphabeticalSort} from "../../../../../utils/helpers";
import {DeleteOutlined} from "@ant-design/icons";
import {
    EquipmentSection,
    SelectedCouriersSection,
    SelectedCouriersTable,
    TableHeader,
    TableRow,
    CourierCell,
    NoteCell,
    ActionCell
} from "./AssignEquipmentModal.styles";

export const AssignEquipmentModal = ({
    visibility,
    recall = () => {},
    onOk = () => {},
    onCancel = () => {},
    changeVisibility = () => {},
    selectedCouriers = [],
    onCourierRemove = () => {},
    singleCourierMode = false
}) => {
    const {t} = useTranslation([]);
    const stores = useStore();
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [loadingEquipments, setLoadingEquipments] = useState(false);
    const [activeCouriers, setActiveCouriers] = useState([]);
    const [courierComments, setCourierComments] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;

    const handleCancel = () => {
        changeVisibility(false);
        onCancel();
    };

    useEffect(() => {
        if (visibility) {
            resetForm();
            loadEquipments();
            setActiveCouriers(selectedCouriers);
            setCourierComments({});
            setCurrentPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibility]);

    const loadEquipments = async () => {
        setLoadingEquipments(true);
        try {
            const response = await stores.equipmentStore.listEquipments({
                limit: 1000,
                page: 1
            });
            if (response?.data?.result) {
                setEquipmentOptions(arrayAlphabeticalSort([...response.data.result], "name"));
            }
        } catch (error) {
            console.error("Error loading equipments:", error);
        } finally {
            setLoadingEquipments(false);
        }
    };

    const handleRemoveCourier = courierId => {
        setActiveCouriers(prev => prev.filter(courier => courier._id !== courierId));
        setCourierComments(prev => {
            const newComments = {...prev};
            delete newComments[courierId];
            return newComments;
        });
        onCourierRemove(courierId);
    };

    const handleCommentChange = (courierId, value) => {
        setCourierComments(prev => ({
            ...prev,
            [courierId]: value
        }));
    };

    const equipmentRows = useMemo(() => {
        if (!Array.isArray(equipmentOptions)) {
            return [];
        }

        return equipmentOptions.flatMap(equipment =>
            (equipment?.variations || []).map(variation => ({
                variationId: variation._id,
                equipmentId: equipment._id,
                equipmentName: equipment.name,
                color: variation.color || "-",
                size: variation.size || "-",
                price: variation.price || 0
            }))
        );
    }, [equipmentOptions]);

    const paginatedCouriers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return activeCouriers.slice(startIndex, endIndex);
    }, [activeCouriers, currentPage]);

    const shouldShowPagination = activeCouriers.length > ITEMS_PER_PAGE;

    const formik = useFormik({
        initialValues: {
            selectedVariations: []
        },
        validationSchema: yup.object().shape({
            selectedVariations: yup
                .array()
                .of(yup.string())
                .min(1, "En az bir ekipman seçmelisiniz")
                .required(t("validation.required", {ns: "common"}))
        }),
        onSubmit: async values => {
            if (activeCouriers.length === 0) {
                message.error("En az bir kurye bulunmalıdır");
                return;
            }

            const selections = equipmentRows.filter(row =>
                values.selectedVariations.includes(row.variationId)
            );

            if (!selections.length) {
                message.error("En az bir ekipman seçmelisiniz");
                return;
            }

            const courierIdsWithAssignmentNotes = activeCouriers.map(courier => ({
                courierId: courier._id,
                assignmentNote: courierComments[courier._id] || ""
            }));

            const equipments = selections.map(item => ({
                equipmentId: item.equipmentId,
                variationId: item.variationId
            }));

            try {
                const response = await stores.equipmentStore.assignEquipmentToMultipleCouriers({
                    courierIdsWithAssignmentNotes,
                    equipments
                });
                if (!response?.success) {
                    message.error("Ekipmanlar Kuryelere Atanamadı");
                    return;
                }

                const equipmentAssignedCouriers = response?.equipmentAssignedCouriers || [];
                changeVisibility(false);
                resetForm();
                onOk({equipmentAssignedCouriers});
            } catch (e) {
                message.error(e.message);
            }
        },
        enableReinitialize: true
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        resetForm,
        setFieldValue,
        setFieldTouched
    } = formik;

    return (
        <Modal
            title={"Ekipman Ata"}
            confirmLoading={isSubmitting}
            visible={visibility}
            onOk={handleSubmit}
            onCancel={handleCancel}
            okText={singleCourierMode ? "Ekipman Ata" : "Seçili Kuryelere Ekipman Ata"}
            cancelText="İptal"
            width={900}
        >
            <Form autoComplete="off" layout="vertical" style={{width: "100%"}}>
                <SelectedCouriersSection>
                    <h4 style={{marginBottom: "12px"}}>Seçili Kuryeler</h4>
                    {activeCouriers.length === 0 ? (
                        <div style={{padding: "16px", textAlign: "center", color: "#999"}}>
                            Seçili kurye bulunmamaktadır
                        </div>
                    ) : (
                        <>
                            <SelectedCouriersTable>
                                <TableHeader>
                                    <div>Kurye Adı</div>
                                    <div>Kurye Bölge</div>
                                    <div>Not</div>
                                    {!singleCourierMode && (
                                        <div style={{textAlign: "right"}}>Aksiyon</div>
                                    )}
                                </TableHeader>
                                {paginatedCouriers.map(courier => (
                                    <TableRow key={courier._id}>
                                        <CourierCell>{courier.fullname || "-"}</CourierCell>
                                        <CourierCell>
                                            {courier.zoneInfo?.map(z => z.zoneName).join(", ") ||
                                                "-"}
                                        </CourierCell>
                                        <NoteCell>
                                            <Input
                                                placeholder="Not giriniz..."
                                                value={courierComments[courier._id] || ""}
                                                onChange={e =>
                                                    handleCommentChange(courier._id, e.target.value)
                                                }
                                                size="small"
                                            />
                                        </NoteCell>
                                        {!singleCourierMode ? (
                                            <ActionCell>
                                                <Button
                                                    type="text"
                                                    danger
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleRemoveCourier(courier._id)}
                                                >
                                                    Kaldır
                                                </Button>
                                            </ActionCell>
                                        ) : null}
                                    </TableRow>
                                ))}
                            </SelectedCouriersTable>
                            {shouldShowPagination && (
                                <div style={{marginTop: "16px", textAlign: "center"}}>
                                    <Pagination
                                        current={currentPage}
                                        pageSize={ITEMS_PER_PAGE}
                                        total={activeCouriers.length}
                                        onChange={page => setCurrentPage(page)}
                                        showSizeChanger={false}
                                        showTotal={(total, range) =>
                                            `${range[0]}-${range[1]} / ${total} kurye`
                                        }
                                        size="small"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </SelectedCouriersSection>

                <div>
                    <h4 style={{marginBottom: "12px"}}>Ekipman Seç</h4>
                    <Form.Item
                        validateStatus={
                            errors.selectedVariations && touched.selectedVariations ? "error" : ""
                        }
                        help={
                            errors.selectedVariations && touched.selectedVariations
                                ? errors.selectedVariations
                                : null
                        }
                    >
                        <EquipmentSection>
                            <Table
                                rowKey="variationId"
                                columns={[
                                    {
                                        title: "Ekipman Adı",
                                        dataIndex: "equipmentName"
                                    },
                                    {
                                        title: "Renk",
                                        dataIndex: "color"
                                    },
                                    {
                                        title: "Beden",
                                        dataIndex: "size"
                                    },
                                    {
                                        title: "Fiyat",
                                        dataIndex: "price",
                                        render: value =>
                                            typeof value === "number"
                                                ? `${value.toLocaleString("tr-TR")} ₺`
                                                : "-"
                                    }
                                ]}
                                dataSource={equipmentRows}
                                pagination={false}
                                size="small"
                                scroll={{y: 260}}
                                loading={loadingEquipments}
                                rowSelection={{
                                    type: "checkbox",
                                    selectedRowKeys: values.selectedVariations,
                                    onChange: selectedRowKeys => {
                                        setFieldTouched("selectedVariations", true, false);
                                        setFieldValue("selectedVariations", selectedRowKeys);
                                    }
                                }}
                            />
                        </EquipmentSection>
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
};
